const STORAGE_KEY = 'canhoto:attribution:v1'
const CONSENT_KEY = 'canhoto:consent:v1'
const MAX_AGE = 90 * 24 * 60 * 60 * 1000
export const attributionKeys = ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

type Touch = { parameters: Record<string, string>; capturedAt: number }
type Attribution = { firstTouch: Touch | null; lastTouch: Touch | null }
const emptyAttribution = (): Attribution => ({ firstTouch: null, lastTouch: null })
let memoryAttribution = emptyAttribution()
let advertisingConsent = false

function readTouch(value: unknown): Touch | null {
  if (!value || typeof value !== 'object' || !('capturedAt' in value) || !('parameters' in value)) return null
  if (typeof value.capturedAt !== 'number' || value.capturedAt > Date.now() || Date.now() - value.capturedAt >= MAX_AGE) return null
  if (!value.parameters || typeof value.parameters !== 'object') return null
  const parameters = Object.fromEntries(Object.entries(value.parameters).filter(([key, item]) => attributionKeys.some(allowed => allowed === key) && typeof item === 'string' && item)) as Record<string, string>
  return Object.keys(parameters).length ? { parameters, capturedAt: value.capturedAt } : null
}

export function getAttribution(): Attribution {
  if (typeof window === 'undefined') return emptyAttribution()
  if (!hasAdvertisingConsent()) return validMemoryAttribution()
  try {
    const value: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    if (!value || typeof value !== 'object') return validMemoryAttribution()
    const attribution = {
      firstTouch: readTouch('firstTouch' in value ? value.firstTouch : null),
      lastTouch: readTouch('lastTouch' in value ? value.lastTouch : null),
    }
    if (!attribution.firstTouch && !attribution.lastTouch) localStorage.removeItem(STORAGE_KEY)
    else if (JSON.stringify(value) !== JSON.stringify(attribution)) localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
    return attribution.firstTouch || attribution.lastTouch ? attribution : validMemoryAttribution()
  } catch { return validMemoryAttribution() }
}

function validMemoryAttribution() {
  memoryAttribution = {
    firstTouch: readTouch(memoryAttribution.firstTouch),
    lastTouch: readTouch(memoryAttribution.lastTouch),
  }
  return memoryAttribution
}

function hasAdvertisingConsent() {
  if (advertisingConsent) return true
  try {
    const value: unknown = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null')
    return Boolean(value && typeof value === 'object' && 'advertising' in value && value.advertising === true)
  } catch { return false }
}

function persist(attribution: Attribution) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution)) } catch { /* Storage may be disabled. */ }
}

export function setAdvertisingConsent(granted: boolean) {
  advertisingConsent = granted
  if (granted) {
    if (memoryAttribution.firstTouch || memoryAttribution.lastTouch) persist(memoryAttribution)
    return
  }
  memoryAttribution = emptyAttribution()
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* Storage may be disabled. */ }
}

export function captureAttribution(search: string) {
  const attribution = getAttribution()
  const query = new URLSearchParams(search)
  const parameters = Object.fromEntries(attributionKeys.flatMap(key => query.get(key) ? [[key, query.get(key)!]] : []))
  if (!Object.keys(parameters).length) return
  // Internal links carry the same campaign; they must not extend its retention.
  if (JSON.stringify(attribution.lastTouch?.parameters) === JSON.stringify(parameters)) return
  const touch = { parameters, capturedAt: Date.now() }
  const next = { firstTouch: attribution.firstTouch ?? touch, lastTouch: touch }
  memoryAttribution = next
  if (hasAdvertisingConsent()) persist(next)
}

export function withAttribution(to: string): string {
  if (typeof window === 'undefined') return to
  const url = new URL(to, window.location.origin)
  if (url.origin !== window.location.origin || !['/planos', '/criar-conta', '/login'].includes(url.pathname)) return to
  const current = new URLSearchParams(window.location.search)
  const stored = getAttribution().lastTouch?.parameters ?? {}
  for (const key of attributionKeys) {
    const value = current.get(key) || stored[key]
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value)
  }
  return `${url.pathname}${url.search}${url.hash}`
}

// TODO(confirmar: backend aceitar attribution): getAttribution() is ready for
// registration integration. RegisterPayload remains unchanged until confirmed.
